import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { ShieldCheck, WarningCircle } from '@phosphor-icons/react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Callback for MB-Management Google-Auth flow (session-32).
 * Emergent redirects here with `#session_id=…` after the admin picks a
 * Google account. We call /admin/mb-registry/verify-google with the intent
 * token stashed in sessionStorage, then bounce back to Admin Settings ->
 * MB Management with a `mb_verified=1` flag so the modal jumps to step 3.
 */
const MBCallback = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const hasProcessed = useRef(false);
  const [status, setStatus] = useState('processing');
  const [error, setError] = useState('');

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    (async () => {
      const hash = location.hash || window.location.hash || '';
      const match = hash.match(/session_id=([^&]+)/);
      const sessionId = match ? decodeURIComponent(match[1]) : null;
      const stash = sessionStorage.getItem('mb_registry_intent');
      let intentToken = null;
      try { intentToken = stash ? JSON.parse(stash).token : null; } catch {}

      if (!sessionId || !intentToken) {
        setStatus('error');
        setError('Verification session expired. Please restart MB action.');
        return;
      }
      try { window.history.replaceState(null, '', window.location.pathname); } catch {}

      try {
        await axios.post(`${API}/admin/mb-registry/verify-google`, {
          intent_token: intentToken, session_id: sessionId,
        });
        navigate('/admin-settings?section=mb&mb_verified=1', { replace: true });
      } catch (err) {
        setStatus('error');
        setError(err.response?.data?.detail || 'Google verification failed.');
      }
    })();
  }, []);  // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-indigo-50 to-indigo-100 p-6">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-xl p-8 text-center">
        {status === 'processing' ? (
          <>
            <div className="mx-auto w-16 h-16 rounded-full bg-indigo-100 flex items-center justify-center mb-4">
              <ShieldCheck size={40} weight="fill" className="text-indigo-600" />
            </div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2" data-testid="mb-cb-processing">
              Verifying with Google for MB Management…
            </h2>
            <p className="text-sm text-gray-500">Please wait while we confirm your identity.</p>
            <div className="mt-6 flex justify-center">
              <div className="w-6 h-6 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin"></div>
            </div>
          </>
        ) : (
          <>
            <div className="mx-auto w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mb-4">
              <WarningCircle size={40} weight="fill" className="text-red-600" />
            </div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2" data-testid="mb-cb-error">
              Verification Failed
            </h2>
            <p className="text-sm text-red-600 mb-6">{error}</p>
            <button
              onClick={() => navigate('/admin-settings?section=mb', { replace: true })}
              className="w-full py-3 rounded-lg bg-indigo-600 text-white font-semibold hover:bg-indigo-700 transition"
              data-testid="mb-cb-back-btn"
            >Back to MB Management</button>
          </>
        )}
      </div>
    </div>
  );
};

export default MBCallback;
